import { Epic } from 'redux-observable';
import { of, from } from 'rxjs';
import { mergeMap, map, catchError, filter } from 'rxjs/operators';
import { RootState } from '../store';
import {
  fetchRenewalMetricsRequested,
  fetchRenewalMetricsSucceeded,
  fetchRenewalMetricsFailed,
  fetchRenewalFiltersRequested,
  fetchRenewalFiltersSucceeded,
  fetchRenewalFiltersFailed,
  fetchRenewalChartsRequested,
  fetchRenewalChartsSucceeded,
  fetchRenewalChartsFailed,
  fetchRenewalPoliciesRequested,
  fetchRenewalPoliciesSucceeded,
  fetchRenewalPoliciesFailed,
  fetchRenewalPerformanceRequested,
  fetchRenewalPerformanceSucceeded,
  fetchRenewalPerformanceFailed,
  refreshRenewalDashboardRequested,
  refreshRenewalDashboardSucceeded,
  refreshRenewalDashboardFailed
} from '../slices/renewalDashboardSlice';
import { RenewalDashboardFilterOptions } from '@/types/renewalDashboard';
import {
  getRenewalDashboardMetrics,
  getRenewalDashboardFilters,
  getRenewalDashboardCharts,
  getRenewalPolicies,
  getRenewalPerformanceStats,
  refreshRenewalDashboard
} from '@/services/api';

// Fetch Renewal Metrics Epic
export const fetchRenewalMetricsEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    filter((action: any) => action.type === fetchRenewalMetricsRequested.type),
    mergeMap((action: any) => {
      const filters = action.payload;
      return from(getRenewalDashboardMetrics(filters)).pipe(
        map((response) => fetchRenewalMetricsSucceeded(response)),
        catchError((error) => of(fetchRenewalMetricsFailed(error.message || 'Failed to fetch renewal metrics')))
      );
    })
  );

// Fetch Renewal Filters Epic
export const fetchRenewalFiltersEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    filter((action: any) => action.type === fetchRenewalFiltersRequested.type),
    mergeMap(() =>
      from(getRenewalDashboardFilters()).pipe(
        map((response) => fetchRenewalFiltersSucceeded(response)),
        catchError((error) => of(fetchRenewalFiltersFailed(error.message || 'Failed to fetch renewal filters')))
      )
    )
  );

// Fetch Renewal Charts Epic
export const fetchRenewalChartsEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    filter((action: any) => action.type === fetchRenewalChartsRequested.type),
    mergeMap((action: any) => {
      const { chartType, filters } = action.payload;
      return from(getRenewalDashboardCharts(chartType, filters)).pipe(
        map((response) => fetchRenewalChartsSucceeded({ chartType, data: response })),
        catchError((error) => of(fetchRenewalChartsFailed(error.message || 'Failed to fetch renewal charts')))
      );
    })
  );

// Fetch Renewal Policies Epic
export const fetchRenewalPoliciesEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    filter((action: any) => action.type === fetchRenewalPoliciesRequested.type),
    mergeMap((action: any) => {
      const { filters, pagination } = action.payload;
      return from(getRenewalPolicies(filters, pagination)).pipe(
        map((response) => fetchRenewalPoliciesSucceeded(response)),
        catchError((error) => of(fetchRenewalPoliciesFailed(error.message || 'Failed to fetch renewal policies')))
      );
    })
  );

// Fetch Renewal Performance Epic
export const fetchRenewalPerformanceEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    filter((action: any) => action.type === fetchRenewalPerformanceRequested.type),
    mergeMap(() =>
      from(getRenewalPerformanceStats()).pipe(
        map((response) => fetchRenewalPerformanceSucceeded(response)),
        catchError((error) => of(fetchRenewalPerformanceFailed(error.message || 'Failed to fetch renewal performance stats')))
      )
    )
  );

// Refresh Renewal Dashboard Epic
export const refreshRenewalDashboardEpic: Epic<any, any, RootState> = (action$) =>
  action$.pipe(
    filter((action: any) => action.type === refreshRenewalDashboardRequested.type),
    mergeMap(() =>
      from(refreshRenewalDashboard()).pipe(
        map(() => refreshRenewalDashboardSucceeded()),
        catchError((error) => of(refreshRenewalDashboardFailed(error.message || 'Failed to refresh renewal dashboard')))
      )
    )
  );

export default [
  fetchRenewalMetricsEpic,
  fetchRenewalFiltersEpic,
  fetchRenewalChartsEpic,
  fetchRenewalPoliciesEpic,
  fetchRenewalPerformanceEpic,
  refreshRenewalDashboardEpic
];
